import * as React from 'react'
import clsx from 'clsx'
import { FormControlContext } from './FormControlContext'
import { SxProps, sx as sxFunc } from '../sx'
import style from './index.module.scss'
import { isFactorElement, isAdornedStart, isFilled } from '../utils'

/**
 * Provides context such as filled/focused/error/required for form inputs.
 * Relying on the context provides high flexibility and ensures that the state always stays
 * consistent across the children of the `FormControl`.
 * This context is used by the following components:
 *
 *  - FormLabel
 *  - FormHelperText
 *  - Input
 *  - InputLabel
 *
 * You can find one composition example below and more going to [the demos](/material-ui/react-text-field/#components).
 *
 * ```jsx
 * <FormControl>
 *   <InputLabel htmlFor="my-input">Email address</InputLabel>
 *   <Input id="my-input" aria-describedby="my-helper-text" />
 *   <FormHelperText id="my-helper-text">We'll never share your email.</FormHelperText>
 * </FormControl>
 * ```
 *
 * ⚠️ Only one `InputBase` can be used within a FormControl because it create visual inconsistencies.
 * For instance, only one input can be focused at the same time, the state shouldn't be shared.
 */

export interface FormControlClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `fullWidth={true}`. */
  fullWidth: string
}

export interface FormControlProps
  extends Partial<React.HTMLAttributes<HTMLDivElement>> {
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default div
   */
  Component?: React.ElementType
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<FormControlClasses>
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  /**
   * If `true`, the label, input and helper text should be displayed in a disabled state.
   * @default false
   */
  disabled?: boolean
  /**
   * If `true`, the label is displayed in an error state.
   * @default false
   */
  error?: boolean
  /**
   * If `true`, the component will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean
  /**
   * If `true`, the component is displayed in focused state.
   */
  focused?: boolean
  /**
   * If `true`, the label will indicate that the `input` is required.
   * @default false
   */
  required?: boolean
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The variant to use.
   * @default 'outlined'
   */
  variant?: 'standard' | 'outlined' | 'filled' | 'outlinedDash'
}

export const FormControl = React.forwardRef<HTMLDivElement, FormControlProps>(
  function FormControl(props, ref) {
    const {
      children,
      classes,
      className,
      color = 'primary',
      Component = 'div',
      disabled = false,
      error = false,
      focused: visuallyFocused,
      fullWidth = false,
      required = false,
      size = 'medium',
      variant = 'outlined',
      sx,
      ...other
    } = props

    const [adornedStart, setAdornedStart] = React.useState(() => {
      // We need to iterate through the children and find the Input in order
      // to fully support server-side rendering.
      let initialAdornedStart = false

      if (children) {
        React.Children.forEach(children, child => {
          if (!isFactorElement(child, ['Input', 'Select'])) {
            return
          }

          const input = isFactorElement(child, ['Select'])
            ? (child as any).props.input
            : child

          if (input && isAdornedStart(input.props)) {
            initialAdornedStart = true
          }
        })
      }
      return initialAdornedStart
    })

    const [filled, setFilled] = React.useState(() => {
      // We need to iterate through the children and find the Input in order
      // to fully support server-side rendering.
      let initialFilled = false

      if (children) {
        React.Children.forEach(children, child => {
          if (!isFactorElement(child, ['Input', 'Select'])) {
            return
          }

          if (isFilled((child as any).props, true)) {
            initialFilled = true
          }
        })
      }

      return initialFilled
    })

    const [focusedState, setFocused] = React.useState(false)
    if (disabled && focusedState) {
      setFocused(false)
    }

    const focused =
      visuallyFocused !== undefined && !disabled
        ? visuallyFocused
        : focusedState

    let registerEffect: any
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const registeredInput = React.useRef(false)
      registerEffect = () => {
        if (registeredInput.current) {
          console.error(
            [
              'There are multiple `InputBase` components inside a FormControl.',
              'This creates visual inconsistencies, only use one `InputBase`.',
            ].join('\n')
          )
        }
        registeredInput.current = true
        return () => {
          registeredInput.current = false
        }
      }
    }

    const childContext = React.useMemo(() => {
      return {
        adornedStart,
        setAdornedStart,
        color,
        disabled,
        error,
        filled,
        focused,
        fullWidth,
        size,
        onBlur: () => {
          setFocused(false)
        },
        onEmpty: () => {
          setFilled(false)
        },
        onFilled: () => {
          setFilled(true)
        },
        onFocus: () => {
          setFocused(true)
        },
        registerEffect,
        required,
        variant,
      }
    }, [
      adornedStart,
      color,
      disabled,
      error,
      filled,
      focused,
      fullWidth,
      registerEffect,
      required,
      size,
      variant,
    ])

    return (
      <FormControlContext.Provider value={childContext}>
        <Component
          className={clsx(
            style.root,
            classes?.root,
            fullWidth && [style.fullWidth, classes?.fullWidth],
            className,
            sxFunc(sx)
          )}
          ref={ref}
          {...other}
        >
          {children}
        </Component>
      </FormControlContext.Provider>
    )
  }
)
